// src/components/layout/CoursesSection.jsx
import React, { useEffect, useRef, useState } from 'react';
import '../../styles/courses.css';

const CoursesSection = ({ college }) => {
  const [isSectionVisible, setIsSectionVisible] = useState(false);
  const [isPostersVisible, setIsPostersVisible] = useState(false);
  
  const sectionRef = useRef(null);
  const postersRef = useRef(null);

  // --- OBSERVER 1: TABLE (Left Side) ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsSectionVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { 
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px" // Wait until 100px inside screen
      }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  // --- OBSERVER 2: POSTERS (Right Side / Bottom Mobile) ---
  // THIS IS THE CRITICAL FIX FOR MOBILE
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPostersVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { 
        // 1. Lower threshold (so we don't need 20% of a huge section visible)
        threshold: 0.1, 
        
        // 2. HUGE NEGATIVE MARGIN: 
        // This effectively moves the "trigger line" 200px UP from the bottom.
        // The animation won't start until the top of the posters is 
        // well inside the viewport.
        rootMargin: "0px 0px -200px 0px" 
      }
    );
    if (postersRef.current) observer.observe(postersRef.current);
    return () => { if (postersRef.current) observer.unobserve(postersRef.current); };
  }, []);

  if (!college || !college.courses) return null;

  const whyChooseUsData = college.whyChooseUsData || [];
  const whyusimg = college.whyusimg || [];
  
  const defaultImages = [
    'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800',
    'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800',
    'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800'
  ];

  return (
    <section 
      ref={sectionRef} 
      className={`courses-section ${isSectionVisible ? 'is-visible' : ''}`}
    >
      <div className="courses-container">
        
        {/* --- LEFT SIDE (Table) --- */}
        <div className="courses-left">
          <div className="courses-header">
            <span className="courses-label">Academic Programs</span>
            <h2 className="courses-title">Popular Courses</h2>
            <p className="courses-subtitle">
              Browse the comprehensive list of popular degrees available at {college.name}.
            </p>
          </div>

          <div className="table-wrapper">
            <table className="courses-table">
              <thead>
                <tr>
                  <th className="th-course">Course Name</th>
                  <th className="th-duration">Duration</th>
                </tr>
              </thead>
              <tbody>
                {college.courses.map((course, index) => {
                  const courseName = typeof course === 'string' ? course : course.name || course.title || 'Course';
                  return (
                    <tr 
                      key={index} 
                      className="course-row"
                      style={{ transitionDelay: `${index * 50 + 200}ms` }}
                    >
                      <td className="td-name">
                        <span className="course-text">{courseName}</span>
                      </td>
                      <td className="td-duration">
                        <span className="duration-badge">{course.duration}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* --- RIGHT SIDE (Posters) --- */}
        <div className="courses-right" ref={postersRef}>
          <div className={`why-us-sidebar ${isPostersVisible ? 'posters-visible' : ''}`}>
            <h3 className="sidebar-title">Why {college.name}?</h3>
            
            <div className="sidebar-posters">
              {whyChooseUsData.map((item, index) => {
                const imageSrc = whyusimg.length > 0 
                  ? whyusimg[index % whyusimg.length]
                  : defaultImages[index % defaultImages.length];
                
                return (
                  <div 
                    key={item.id} 
                    className="poster-card"
                    // On mobile, delays are short (200ms) because we relied on rootMargin to delay the start
                    style={{ transitionDelay: `${index * 200}ms` }}
                  >
                    <img src={imageSrc} alt={item.title} className="poster-bg" />
                    <div className="poster-gradient"></div>
                    <div className="poster-content">
                      <div className="poster-accent-line"></div>
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CoursesSection;  